// Global scope — the shell-level competition/season filter (ui-conventions.md
// #97, owner ruling 2026-08-22). ONE mechanism: this store owns the scope
// state and its URL params (`gc` competitions, `gs` seasons — '<id>' include /
// '!<id>' exclude, the same encoding every facet param uses); the top bar
// renders it with the same tri-state optics as the filter bar; every
// applicable view narrows through the predicates below. Views NEVER
// re-implement scope matching — they call `eventInScope`/`matchesCompetition`/
// `matchesSeason`, so the rule cannot fork per view.
//
// Persistence: the URL is the source of truth (#5 — shareable); the router
// guard installed by `installGlobalScope` re-injects the params on
// navigations that would lose them, so the scope survives every transition
// (#1) until explicitly cleared.
//
// Server-side surfaces that cannot honor the scope yet (persons search,
// accreditation lists) are a SPEC task, not a client approximation (#97) —
// views without a scope-capable surface say so rather than pretending.
import { defineStore } from 'pinia'
import type { Router } from 'vue-router'
import {
  provideGlobalParams,
  registerGlobalParams,
  writeGlobalParams,
} from 'src/composables/useViewState'

export interface TriState {
  include: string[]
  exclude: string[]
}

const GC = 'gc'
const GS = 'gs'
registerGlobalParams(GC, GS)

function decodeTri(raw: string | null | undefined): TriState {
  const out: TriState = { include: [], exclude: [] }
  if (!raw) return out
  for (const part of raw.split(',')) {
    if (!part) continue
    if (part.startsWith('!')) out.exclude.push(part.slice(1))
    else out.include.push(part)
  }
  return out
}

function encodeTri(t: TriState): string {
  return [...t.include, ...t.exclude.map((v) => `!${v}`)].join(',')
}

/** Tri-state pass: include set nonempty → value must be in it; exclusion
 *  always knocks out. Empty scope passes everything. */
function triPasses(t: TriState, value: string): boolean {
  if (t.exclude.includes(value)) return false
  if (t.include.length > 0) return t.include.includes(value)
  return true
}

// Module-level rather than store state: Router's deeply-readonly type fights
// Pinia's state unwrapping, and the router is process-singleton anyway.
let boundRouter: Router | null = null

export const useGlobalScopeStore = defineStore('globalScope', {
  state: () => ({
    competitions: { include: [], exclude: [] } as TriState,
    seasons: { include: [], exclude: [] } as TriState,
    /** The one-shot shell default was decided for this session (see
     *  applyDefault) — never re-applied, so clearing it stays cleared. */
    defaultConsidered: false,
  }),

  getters: {
    hasScope(s): boolean {
      return (
        s.competitions.include.length > 0 ||
        s.competitions.exclude.length > 0 ||
        s.seasons.include.length > 0 ||
        s.seasons.exclude.length > 0
      )
    },
    /** Just the season dimension — CompetitionsPage/TenantsPage's own rule
     *  (lib/globalScopeRules.ts) only cares whether a season scope EXISTS,
     *  not what it matches (that's still matchesSeason's job). */
    hasSeasonScope(s): boolean {
      return s.seasons.include.length > 0 || s.seasons.exclude.length > 0
    },
    matchesCompetition() {
      return (competitionId: string) => triPasses(this.competitions, competitionId)
    },
    matchesSeason() {
      return (season: number | string) => triPasses(this.seasons, String(season))
    },
    /** The one event predicate every view narrows through (#97). */
    eventInScope() {
      return (event: { competition: { id: string }; season: number | string }) =>
        this.matchesCompetition(event.competition.id) && this.matchesSeason(event.season)
    },
  },

  actions: {
    /** Replace one dimension (the bar's FacetList binds through this). */
    setCompetitions(t: TriState) {
      this.competitions = { include: [...t.include], exclude: [...t.exclude] }
      this.writeToRoute()
    },
    setSeasons(t: TriState) {
      this.seasons = { include: [...t.include], exclude: [...t.exclude] }
      this.writeToRoute()
    },
    reset() {
      this.competitions = { include: [], exclude: [] }
      this.seasons = { include: [], exclude: [] }
      this.writeToRoute()
    },

    /** The shell default (owner ruling 2026-08-26, #107): applied ONCE per
     *  session, only when nothing is set — a URL-carried scope, a scope the
     *  user set before data landed, or an earlier decision all win. The
     *  default lands as ordinary visible, removable scope state (#96) and
     *  clearing it stays cleared for the session. The VALUES come from the
     *  shell (lib/globalScopeRules.computeDefaultGlobalScope) — this store
     *  never reads coreData/auth itself. */
    applyDefault(competitions: string[], seasons: string[]) {
      if (this.defaultConsidered) return
      this.defaultConsidered = true
      if (this.hasScope) return
      this.competitions = { include: [...competitions], exclude: [] }
      this.seasons = { include: [...seasons], exclude: [] }
      this.writeToRoute()
    },

    readFromQuery(query: Record<string, unknown>) {
      const one = (v: unknown): string | null => (typeof v === 'string' ? v : Array.isArray(v) ? String(v[0] ?? '') : null)
      this.competitions = decodeTri(one(query[GC]))
      this.seasons = decodeTri(one(query[GS]))
    },

    /**
     * Puts the scope on the URL.
     *
     * Delegated to useViewState rather than merged here (fsgadmin/fsgnext#85):
     * a `{ ...route.query }` snapshot goes stale the moment another
     * `router.replace` is resolving its guards, and writing it back
     * resurrected whatever view param that replace was carrying — a ranking
     * picked on the scoring results page snapped straight back to the
     * previous one. Every param is now written from its own owner instead.
     */
    writeToRoute() {
      const router = boundRouter
      if (!router) return
      writeGlobalParams(router)
    },
  },
})

/**
 * Wires the store to the router: hydrates from the initial URL, and re-injects
 * the scope params on any navigation that would silently drop them (a nav
 * click targets a bare route; the scope must survive the transition, #1).
 * Called once from the router setup.
 */
export function installGlobalScope(router: Router): void {
  // LAZY store access: this runs inside Quasar's router wrapper, which may
  // execute before Pinia is installed on the app (the same boot-order trap
  // src/stores/index.ts documents for boot files). Every callback resolves
  // the store at call time — by the first navigation, Pinia is active.
  boundRouter = router
  let cached: ReturnType<typeof useGlobalScopeStore> | null = null
  const store = () => {
    if (!cached) cached = useGlobalScopeStore()
    return cached
  }

  // useViewState canonicalizes the whole query, and these two keys are the
  // half it does not own. It reads them from HERE rather than from the route
  // it is about to overwrite — see the #85 note in useViewState.ts.
  provideGlobalParams(router, () => {
    const s = store()
    const out: Record<string, string> = {}
    const gc = encodeTri(s.competitions)
    const gs = encodeTri(s.seasons)
    if (gc) out[GC] = gc
    if (gs) out[GS] = gs
    return out
  })

  router.isReady().then(
    () => store().readFromQuery(router.currentRoute.value.query),
    () => {},
  )

  router.beforeEach((to, from) => {
    // An explicit gc/gs in the target wins (shared-link case) — hydrate from
    // it. Otherwise carry the active scope over.
    const s = store()
    if (to.query[GC] !== undefined || to.query[GS] !== undefined) {
      s.readFromQuery(to.query)
      return true
    }
    if (!s.hasScope) return true
    if (from.name === undefined && to.name === undefined) return true
    const query = { ...to.query } as Record<string, string>
    const gc = encodeTri(s.competitions)
    const gs = encodeTri(s.seasons)
    if (gc) query[GC] = gc
    if (gs) query[GS] = gs
    return { path: to.path, query, hash: to.hash, replace: false }
  })
}
